/** The plan chooser (spec §9.1): shown whenever the workspace may not spend. Copy is keyed
 * on why; prices come from the checkout handshake, never from copy — Paddle's overlay is the
 * price display. */
import { useState } from "react";
import type { Api, EntitlementReason } from "../api/api";
import { brand } from "../brand";
import type { CheckoutInput } from "./paddle";
import { takePendingCode } from "../site/pendingCode";
import "../styles/account.css";

type Plan = "starter" | "full";

/** Full is the default choice (design v2 § 8): the ink border and the filled button. */
const PLANS: {
  plan: Plan;
  name: string;
  tag?: string;
  subjects: string;
  pages: string;
  papers: string;
}[] = [
  {
    plan: "starter",
    name: "Starter",
    subjects: "2 subjects",
    pages: "1,000 pages",
    papers: "5 papers",
  },
  {
    plan: "full",
    name: "Full",
    tag: "for a full exam year",
    subjects: "6 subjects",
    pages: "5,000 pages",
    papers: "30 papers",
  },
];
const CHOSEN: Plan = "full";

const WHY: Record<EntitlementReason, { title: string; body: string }> = {
  ok: { title: "Choose a plan", body: "" },
  never_subscribed: {
    title: "Start a 7-day free trial",
    body:
      "A card is required. The trial covers one book of up to 400 pages and two practice " +
      "papers — making this material costs real money, and we would rather say so than " +
      "pretend otherwise. Nothing is charged until the seven days are up.",
  },
  lapsed: {
    title: "Your subscription ended",
    body:
      "Your material is still here — every flashcard, lesson and paper — and it stays yours " +
      "to study. Subscribe again to upload more or write new papers.",
  },
  code_expired: {
    title: "Your access code has ended",
    body:
      "Everything you made is still here to study. Have another code? Enter it below, or " +
      "choose a plan.",
  },
};

export function money(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount / 100);
}

export function SubscribeScreen({
  api,
  reason,
  workspaceId,
  promo,
  trialOpen,
  openCheckout,
  onRedeemed,
}: {
  api: Api;
  reason: EntitlementReason;
  workspaceId: string;
  promo: string | null;
  /** The closed beta builds with this off: no plans, no checkout, the code is the door. */
  trialOpen: boolean;
  openCheckout: (input: CheckoutInput) => Promise<void>;
  onRedeemed: () => void;
}) {
  const [price, setPrice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A code typed on the landing before signing in waits here, taken once.
  const [code, setCode] = useState(() => takePendingCode() ?? "");
  const [busy, setBusy] = useState(false);
  const why = WHY[reason];

  async function start(plan: Plan) {
    setBusy(true);
    setError(null);
    setPrice(null);
    try {
      const r = await api.checkout(plan, promo);
      if (!r.ok) {
        setError(
          r.error === "price_not_configured"
            ? "Payments are not set up yet — try again later."
            : "Something went wrong starting the checkout. Try again.",
        );
        return;
      }
      const name = PLANS.find((p) => p.plan === plan)?.name ?? plan;
      setPrice(
        `${name}: ${money(r.data.amount, r.data.currency)} a month` +
          (r.data.trial_days ? ` after a ${r.data.trial_days}-day free trial` : ""),
      );
      await openCheckout({
        priceId: r.data.price_id,
        customerId: r.data.customer_id,
        workspaceId,
        promo: r.data.promo,
        successUrl: `${window.location.origin}/billing/return`,
      });
    } catch {
      setError("The checkout could not be opened. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function redeem() {
    setBusy(true);
    setError(null);
    const r = await api.redeem(code.trim());
    setBusy(false);
    if (!r.ok) {
      setError(
        r.error === "already_subscribed"
          ? "This account already has a subscription, so the code was not used."
          : "That is not a valid code. Check it and try again.",
      );
      return;
    }
    onRedeemed();
  }

  // The code is an answer line with its button beside it, open from the start: a disclosure
  // hid the one thing a beta tester or a teacher came to do.
  const codeForm = (
    <form
      className="redeem__form"
      onSubmit={(e) => {
        e.preventDefault();
        void redeem();
      }}
    >
      <label htmlFor="access-code" className="redeem__label">
        Have a code? Teachers and testers get one.
      </label>
      <div className="redeem__row">
        <input
          id="access-code"
          aria-label="Access code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder="XXXX - XXXX - XXXX"
        />
        <button type="submit" className="button" disabled={busy || !code.trim()}>
          Redeem
        </button>
      </div>
    </form>
  );

  // The closed beta (spec §5): nobody pays, so the plans and the checkout are not rendered
  // at all rather than disabled. A code that has ended keeps its own heading — the reader
  // already knows what this is and needs to know what happened.
  if (!trialOpen) {
    return (
      <main className="subscribe">
        <h1>{reason === "code_expired" ? why.title : "Access is by invitation"}</h1>
        <p className="subscribe__lede">
          {brand.name} is in a closed beta. If you have an access code, enter it below; if you would
          like one, write to {brand.supportEmail}.
        </p>
        <div className="redeem">{codeForm}</div>
        {error && (
          <p role="alert" className="field-error">
            {error}
          </p>
        )}
      </main>
    );
  }

  return (
    <main className="subscribe">
      <h1>{why.title}</h1>
      {why.body && <p className="subscribe__lede">{why.body}</p>}
      <div className="plans">
        {PLANS.map((c) => (
          <section
            key={c.plan}
            className={`plan${c.plan === CHOSEN ? " plan--chosen" : ""}`}
            aria-labelledby={`plan-${c.plan}`}
          >
            <div className="plan__head">
              <h2 id={`plan-${c.plan}`}>{c.name}</h2>
              {c.tag && <span className="plan__tag">{c.tag}</span>}
            </div>
            <dl className="plan__caps">
              <div>
                <dt>Subjects</dt>
                <dd>{c.subjects}</dd>
              </div>
              <div>
                <dt>Textbook a month</dt>
                <dd>{c.pages}</dd>
              </div>
              <div>
                <dt>Papers a month</dt>
                <dd>{c.papers}</dd>
              </div>
            </dl>
            <p className="plan__note">Flashcards and lessons for every chapter.</p>
            <button
              type="button"
              className={`button${c.plan === CHOSEN ? " button--primary" : ""}`}
              disabled={busy}
              onClick={() => void start(c.plan)}
            >
              Start {c.name}
            </button>
          </section>
        ))}
      </div>
      {price && <p className="subscribe__price">{price}</p>}
      {promo && (
        <p className="subscribe__small">Discount code {promo} will be applied at checkout.</p>
      )}
      <p className="subscribe__small">
        One subscription per household. Cancel any time from your account. The price is shown before
        you pay.
      </p>
      <div className="redeem">{codeForm}</div>
      {error && (
        <p role="alert" className="field-error">
          {error}
        </p>
      )}
    </main>
  );
}
